import { useState } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { ApproveReceivableOptions } from "../providers/supabase/invoicing";
import { formatMxn } from "../quoter/PlanSchedule";
import type { DialogSubmit } from "../operations/OperationDocumentDialogs";
import type { ReceivableSummary } from "./invoicingTypes";
import {
  buildApprovalPreview,
  initialApprovalDraft,
  isDeveloperPaid,
  type ApprovalDraft,
} from "./invoicingScreenRules";

const FIELD_CLASS = "h-11 sm:h-9";

/**
 * Dirección's VoBo of a receivable: editable installment number, commission %
 * and amount (IVA and total recomputed live in integer cents) and a live
 * preview of the concept the server will freeze. A developer-paid (BPO)
 * receivable renders the project's CURRENT template (`template`); a buyer-paid
 * (corretaje) one has none, so the concept is typed here and required.
 */
export const ReceivableApproveDialog = ({
  receivable,
  template,
  onClose,
  onSubmit,
}: {
  receivable: ReceivableSummary;
  /** The project's current concept template (null for a corretaje receivable or while it loads). */
  template: string | null;
  onClose: () => void;
  onSubmit: DialogSubmit<ApproveReceivableOptions>;
}) => {
  const translate = useTranslate();
  const [draft, setDraft] = useState<ApprovalDraft>(() =>
    initialApprovalDraft(receivable),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const developerPaid = isDeveloperPaid(receivable);
  const preview = buildApprovalPreview(receivable, template, draft);
  const patch = (change: Partial<ApprovalDraft>) =>
    setDraft((current) => ({ ...current, ...change }));

  const confirm = async () => {
    if (preview.errors.length > 0 || preview.installment == null) return;
    setBusy(true);
    setError(null);
    const message = await onSubmit({
      installmentNumber: preview.installment,
      commissionPct: preview.pct ?? undefined,
      amount: preview.amount ?? undefined,
      conceptOverride: developerPaid ? undefined : draft.conceptText.trim(),
    });
    setBusy(false);
    if (message) setError(message);
  };

  const shownAmount =
    draft.amountText ??
    (preview.amount == null ? "" : preview.amount.toFixed(2));

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translate("crm.invoicing.approve.title")}</DialogTitle>
          <DialogDescription>
            {translate("crm.invoicing.approve.description", {
              operation: receivable.operation_id,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="approve-installment">
              {translate("crm.invoicing.fields.installment")}
            </Label>
            <Input
              id="approve-installment"
              className={FIELD_CLASS}
              inputMode="numeric"
              value={draft.installmentText}
              onChange={(event) =>
                patch({ installmentText: event.target.value })
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="approve-pct">
              {translate("crm.invoicing.fields.commission_pct")}
            </Label>
            <Input
              id="approve-pct"
              className={FIELD_CLASS}
              inputMode="decimal"
              value={draft.pctText}
              onChange={(event) => patch({ pctText: event.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="approve-amount">
              {translate("crm.invoicing.fields.amount")}
            </Label>
            <Input
              id="approve-amount"
              className={FIELD_CLASS}
              inputMode="decimal"
              value={shownAmount}
              onChange={(event) => patch({ amountText: event.target.value })}
            />
          </div>
        </div>

        {preview.pctSource ? (
          <p data-testid="approve-pct-source" className="text-sm">
            {translate(
              `crm.invoicing.approve.pct_source.${preview.pctSource}`,
              {
                pct: receivable.commission_pct ?? "",
              },
            )}
          </p>
        ) : null}

        {!developerPaid ? (
          <div className="space-y-1">
            <Label htmlFor="approve-concept">
              {translate("crm.invoicing.approve.concept_label")}
            </Label>
            <Textarea
              id="approve-concept"
              value={draft.conceptText}
              onChange={(event) => patch({ conceptText: event.target.value })}
            />
            <p className="text-sm text-muted-foreground">
              {translate("crm.invoicing.approve.concept_hint")}
            </p>
          </div>
        ) : null}

        <dl className="grid grid-cols-3 gap-3 text-sm tabular-nums">
          <div>
            <dt className="text-muted-foreground">
              {translate("crm.invoicing.fields.subtotal")}
            </dt>
            <dd data-testid="approve-subtotal">
              {preview.breakdown ? formatMxn(preview.breakdown.subtotal) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {translate("crm.invoicing.fields.iva", {
                pct: receivable.iva_pct,
              })}
            </dt>
            <dd data-testid="approve-iva">
              {preview.breakdown
                ? formatMxn(preview.breakdown.iva_amount)
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {translate("crm.invoicing.fields.total")}
            </dt>
            <dd data-testid="approve-total" className="font-semibold">
              {preview.breakdown
                ? formatMxn(preview.breakdown.total_amount)
                : "—"}
            </dd>
          </div>
        </dl>

        <div className="space-y-1">
          <p className="text-sm font-medium">
            {translate("crm.invoicing.approve.preview")}
          </p>
          <p
            data-testid="approve-concept-preview"
            className="rounded-md border bg-muted/40 p-3 text-sm break-words"
          >
            {preview.concept ?? "—"}
          </p>
        </div>

        {preview.errors.length > 0 ? (
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {preview.errors.map((key) => (
              <li key={key}>
                {translate(`crm.invoicing.approve.errors.${key}`)}
              </li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-[#B3321C]">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {translate("crm.operations.documents.cancel")}
          </Button>
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            disabled={busy || preview.errors.length > 0}
            onClick={confirm}
          >
            {translate("crm.invoicing.approve.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
